import { Ajax } from '@shared/Ajax';
import { Drawer } from '@shared/ui-native/Drawer';
import { Notify } from '@shared/ui-native/Notify';
import { FiletreeSelect } from '@shared/ui-react/FiletreeSelect';
import { autorun, makeObservable, observable, reaction } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { mapRootStore, mapUI, mapWrapper } from '../../map';
import { MassGridLayer } from '../mass-grid/MassGridLayer';
import './MassGridEditor.scss';

import { fitFeatures } from '@openlayers/ol-utils';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import Feature from 'ol/Feature';
import { transform as olTransform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';


/**
 * @author NikolaV
 */

declare var RotateFeatureInteraction: any;

export const MassGridEditor = {
    open(onSaved?: (s: any) => void, folderId?: number) {
        const el = Drawer.open();
        ReactUtils.safeRender(<GridEditorForm onSaved={onSaved} folderId={folderId} />, el);
    },
    edit(sheet: Mass.SheetArea, onSaved?: (s: any) => void) {
        const el = Drawer.open();

        ReactUtils.safeRender(<GridEditorForm
            id={sheet.id}
            name={sheet.name}
            folderId={sheet.folderId}
            cellWidth={sheet.externalData.cellWidth}
            rotation={sheet.externalData.rotation}
            calculationLayerId={sheet.model1Id}
            referenceLayerId={sheet.model2Id}
            limitLayerId={sheet.model3Id}
            onSaved={onSaved}
        />, el);
    },
    close() {
        const el = Drawer.close();
        ReactUtils.safeUnmountComponentAtNode(el);
    },
};

interface IProps {
    id?: number;
    folderId?: number;
    name?: string;
    cellWidth?: number;
    rotation?: number;
    calculationLayerId?: number;
    referenceLayerId?: number;
    limitLayerId?: number;

    onSaved?: (s: any) => void;
}

@observer
class GridEditorForm extends React.Component<IProps> {
    static defaultProps: IProps = {
        name: kuura.getMessage('mass.task_type.grid') + " " + Date.now(),
        cellWidth: 50,
        rotation: 20,
        calculationLayerId: -1,
        referenceLayerId: -1,
        limitLayerId: -1,
        folderId: -1,
    };

    @observable editable = true;

    constructor(props: IProps) {
        super(props);
        makeObservable(this);
    }

    get isEditMode(): boolean {
        return !!this.props.id;
    }

    @observable size = this.props.cellWidth;
    @observable rotation = this.props.rotation;

    @observable name: string = this.props.name;
    @observable folderId: number = this.props.folderId || mapRootStore.treeData.id;
    @observable calculationLayer = this.props.calculationLayerId;
    @observable referenceLayer = this.props.referenceLayerId;
    @observable limitLayer = this.props.limitLayerId;

    @observable totalCells = 0;

    updateSizeDebounced = _.debounce(val => this.size = utils.clamp(this.MIN_SIZE, val, this.MAX_SIZE), 500);

    updateRotationDebounced = _.debounce(val => this.rotation = utils.clamp(0, val, 360), 500);

    private readonly MIN_SIZE = 10;
    private readonly MAX_SIZE = 200;

    private gridFeature: MassGridLayer;
    private modelsSource: VectorSource;
    private models: Feature[] = [];

    componentDidMount() {
        this.gridFeature = new MassGridLayer(mapWrapper.getInstance());

        mapWrapper.featureLayers.models.layerSource.on('addfeature', () => {
            this.setModels();
        });
        mapWrapper.featureLayers.models.layerSource.on('removefeature', () => {
            console.log('removefeature');
            this.setModels();
        });
        reaction(() => [this.size, this.rotation], () => this.draw());

        console.log('this.folderId:', this.props);

        autorun(this.loadModels, { delay: 1 });
    }

    componentWillUnmount() {
        this.gridFeature.destroy();
        mapWrapper.featureLayers.models.layerSource.un('addfeature', this.draw);
        mapWrapper.featureLayers.models.layerSource.un('removefeature', this.draw);
    }

    draw = () => {
        this.totalCells = this.gridFeature.draw(this.size, this.rotation, this.modelsSource);
    };

    submit = () => {
        const { cells, bbExtent } = this.gridFeature.getState();
        const rows = _.groupBy(cells, c => c.y);

        const lines = Object.entries(rows).forEach(([key, val]) => {
            val.sort((c1, c2) => c1.x - c2.x);

            val.forEach(r => {
                r.p1 = olTransform(r.p1, 'EPSG:3857', 'EPSG:4326');
                r.p2 = olTransform(r.p2, 'EPSG:3857', 'EPSG:4326');
                r.p3 = olTransform(r.p3, 'EPSG:3857', 'EPSG:4326');
                r.p4 = olTransform(r.p4, 'EPSG:3857', 'EPSG:4326');
            });
        });

        const data = {
            id: this.props.id,
            bbExtent: [
                ...olTransform([bbExtent[0], bbExtent[1]], 'EPSG:3857', 'EPSG:4326'),
                ...olTransform([bbExtent[2], bbExtent[3]], 'EPSG:3857', 'EPSG:4326'),
            ],
            cellWidth: this.size,
            rotation: this.rotation,
            referenceLayerId: this.referenceLayer,
            calculationLayerId: this.calculationLayer,
            limitLayerId: this.limitLayer,
            name: this.name,
            folderId: this.folderId,
            rows,
        };

        console.log(data);

        Ajax({
            method: 'post',
            url: 'api/1/mass/grid.json',
            data,
            processData: false,
        }).done(resp => {
            this.editable = false;
            Notify.success();

            if (typeof this.props.onSaved === 'function') {
                this.props.onSaved(resp);
            }
        })
            .fail(Notify.error);
    };

    loadModels = () => {
        console.log('load models');
        mapUI.mapPanels.panels.models.clearSelection();
        mapUI.mapPanels.panels.models.checkIds([this.calculationLayer, this.referenceLayer, this.limitLayer]);
    };

    setModels = _.debounce(() => {
        const orig = [
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.calculationLayer })[0],
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.referenceLayer })[0],
            mapWrapper.featureLayers.models.getFeaturesBySource({ modelId: this.limitLayer })[0]];

        this.models = orig.map(f => f ? f.clone() : f);
        this.modelsSource = new VectorSource({ features: this.models.filter(Boolean) });

        // todo figure out why 'removefeature' fires
        this.draw();
        this.fitFeatures();
    }, 100);

    fitFeatures = _.debounce(() => {
        fitFeatures(mapWrapper.getInstance(), this.models);
    }, 300);

    filetreeFilterModels = item => {
        if (item.tolerance) return true; // folder
        return kuuraModel.isModel(item);
    }

    render() {
        return (
            <div>
                <header>
                    <div className='padded'>
                        <h1 style={{ margin: 0 }}>{__('mass.grid_task')}</h1>
                    </div>
                </header>

                <div className={!this.editable ? 'fade-disable' : null}>
                    <div className='form-group'>
                        <label className='styled'>{__('general.name')}</label>
                        <input type='text' value={this.name} onChange={e => this.name = e.currentTarget.value} />
                        <label className='styled'>{__('general.folder')}</label>
                        <FiletreeSelect name='folderId'
                            filetreeData={mapRootStore.treeData} includeRoot={true} firstEmpty={false}
                            value={this.folderId} onChange={id => this.folderId = id} />
                    </div>

                    <div className='form-group'>
                        <label className='styled'>{__('mass.grid_cell_width')}</label>
                        <div className='flex-row'>
                            <input type='range' min='10' max='200' value={this.size}
                                onChange={e => this.size = utils.clamp(this.MIN_SIZE, e.currentTarget.value as any, this.MAX_SIZE)} />

                            <input type='number' min='10' max='200' value={this.size}
                                onChange={e => this.size = utils.clamp(this.MIN_SIZE, e.currentTarget.value as any, this.MAX_SIZE)}
                                style={{ margin: '0 1em', width: 80 }} /> m
                        </div>

                        <br />

                        <label className='styled'>{__('mass.grid_cell_rotation')}</label>
                        <div className='flex-row'>
                            <input type='range' min='0' max='90' step={1} value={this.rotation}
                                onChange={e => this.rotation = (e.currentTarget.value as any)} />
                            <input type='number' min='0' max='90' step={1} value={this.rotation}
                                onChange={e => this.rotation = (e.currentTarget.value as any)}
                                style={{ margin: '0 1em', width: 80 }} /> deg
                        </div>

                        <div style={{ fontSize: '11px', marginTop: 7 }}>{__('mass.grid.total_cells')}: {this.totalCells}</div>
                    </div>

                    <div className='form-group'>
                        <label className='styled'>{__('mass.top_surface')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='calculationLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                onChange={id => this.calculationLayer = id ? parseInt(id) : -1} selectedId={this.calculationLayer} />
                        </div>
                        <br />
                        <label className='styled'>{__('mass.bottom_surface')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='referenceLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                onChange={id => this.referenceLayer = id ? parseInt(id) : -1} selectedId={this.referenceLayer} />
                        </div>
                        <br />
                        <label className='styled'>{__('mass.limit_surface')}</label>
                        <div className='flex-row'>
                            <FiletreeSelectReact selectable='file' formFieldName='limitLayer' includeRoot={true} canClearValue={true} hideEmptyFolders={true}
                                treeData={mapRootStore.treeData} childFilter={this.filetreeFilterModels} nullValue={'-1'}
                                onChange={id => this.limitLayer = id ? parseInt(id) : -1} selectedId={this.limitLayer} />
                        </div>
                    </div>
                </div>

                <footer style={{ display: 'flex' }}>
                    <button onClick={MassGridEditor.close} className='ik-button-secondary--subtle'><i className='iki iki-chevron-left'></i> {__('general.close')}</button>
                    <button onClick={this.submit} disabled={!this.editable} style={{ marginLeft: 'auto' }} className='ik-button-primary'>{__(this.isEditMode ? 'general.save' : 'general.add')}</button>
                </footer>
            </div>
        );
    }
}
